/**
 * Money formatting utilities
 */

import { DEFAULT_CURRENCY } from "@/constants/common";
import type { Money } from "@/domain/core";

/**
 * Formats a money amount with currency symbol
 * @param amount - The amount to format
 * @param currency - The currency code (defaults to "CAD")
 * @param locale - The locale for formatting (defaults to "en-CA")
 * @returns Formatted currency string
 */
export const formatMoney = (
  amount: number,
  currency: string = DEFAULT_CURRENCY,
  locale: string = "en-CA"
): string => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency
  }).format(amount);
};

/**
 * Formats a money amount with currency symbol for display in tables/cards
 * @param amount - The amount to format
 * @param currency - The currency code (defaults to "CAD")
 * @param locale - The locale for formatting (defaults to "en-CA")
 * @returns Formatted currency string
 */
export const formatMoneyDisplay = (
  amount: number,
  currency: string = DEFAULT_CURRENCY,
  locale: string = "en-CA"
): string => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
};

/**
 * Formats a money amount for compact display (e.g., $1.2K, $1.2M)
 * @param amount - The amount to format
 * @param currency - The currency code (defaults to "CAD")
 * @param locale - The locale for formatting (defaults to "en-CA")
 * @returns Formatted currency string with compact notation
 */
export const formatMoneyCompact = (
  amount: number,
  currency: string = DEFAULT_CURRENCY,
  locale: string = "en-CA"
): string => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    notation: "compact",
    maximumFractionDigits: 1
  }).format(amount);
};

/**
 * Formats a money amount for input fields (no currency symbol)
 * @param amount - The amount to format
 * @param locale - The locale for formatting (defaults to "en-CA")
 * @returns Formatted number string
 */
export const formatMoneyInput = (
  amount: number,
  locale: string = "en-CA"
): string => {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
};

/**
 * Parses a formatted money string back to a number
 * @param formattedAmount - The formatted amount string
 * @param locale - The locale for parsing (defaults to "en-CA")
 * @returns Parsed number
 */
export const parseMoney = (formattedAmount: string): number => {
  // Remove currency symbols and parse as number
  const cleanAmount = formattedAmount.replace(/[^\d.,]/g, "");
  return parseFloat(cleanAmount.replace(",", ".")) || 0;
};

/**
 * Gets the currency symbol for a given currency code
 * @param currency - The currency code
 * @param locale - The locale for formatting (defaults to "en-CA")
 * @returns Currency symbol
 */
export const getCurrencySymbol = (
  currency: string = DEFAULT_CURRENCY,
  locale: string = "en-CA"
): string => {
  return (
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })
      .formatToParts(0)
      .find((part) => part.type === "currency")?.value || currency
  );
};

/**
 * Validates if a currency code is supported
 * @param currency - The currency code to validate
 * @returns True if the currency is supported
 */
export const isValidCurrency = (currency: string): boolean => {
  try {
    new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency
    }).format(0);
    return true;
  } catch {
    return false;
  }
};


/**
 * Converts a money object to a positive money object
 * @param rate - The money object to convert
 * @returns The positive money object
 */
export function toPositiveMoney(rate?: Money): Money | undefined {
  if (!rate || !(rate.amount > 0) || !rate.currency) {
    return undefined;
  }
  return rate;
}